import sortBy from 'lodash/sortBy';
import { useTranslations } from 'next-intl';

import {
  findAllVariants,
  findImageAsset,
  getProductFinish,
  getProductVariantSize,
  isVariantInStock,
} from '@/app/utils';
import type { Product } from '@/queries/types/graphql';

import type { FinishOption, SizeOption } from './types';

interface ProductOptionsData {
  finishOptions: FinishOption[];
  sizeOptions: SizeOption[];
}

type ImageType = 'selector' | 'angle';

interface UseProductOptionDataOptions {
  selectedSku?: string;
  imageType?: ImageType;
}

export const getOptionImgSrc = (
  product: Product,
  imageType?: ImageType,
): string => {
  if (imageType === 'selector') {
    return (
      findImageAsset(product, 'finish-selector') ??
      findImageAsset(product, 'angle') ??
      ''
    );
  }

  return (
    findImageAsset(product, 'angle') ??
    // TODO: remove once Gen3 is extinct
    product.images?.[1]?.originalSrc ??
    ''
  );
};

export const useFinishOptions = (
  products: Product[],
  options: UseProductOptionDataOptions = {},
) => {
  const t = useTranslations();
  const { imageType } = options;

  return products.reduce((list, product) => {
    // Hide out-of-stock finishes entirely: RingFinishChoices has no per-item
    // stock support, and hiding keeps both select UIs telling the same story.
    if (product.inStock === false) return list;

    const finish = getProductFinish(product);

    if (!finish) return list;

    // if finish is already in list, skip it
    if (list.some((item) => item.value === finish)) {
      return list;
    }

    return [
      ...list,
      {
        imgSrc: getOptionImgSrc(product, imageType),
        label: t(product.colorMsgKey),
        value: finish,
      },
    ];
  }, [] as FinishOption[]);
};

export const useSizeOptions = (
  products: Product[],
  options: UseProductOptionDataOptions = {},
): SizeOption[] => {
  const { selectedSku } = options;
  const variants = findAllVariants(products, selectedSku);

  const allSizes = variants
    ?.map((variant) => ({
      size: getProductVariantSize(variant),
      inStock: isVariantInStock(variant),
    }))
    // filter out any bad sizes
    .filter(
      (option): option is { size: string; inStock: boolean } =>
        option.size !== undefined,
    )
    .filter(({ size }) => !['Size later'].includes(size))
    .map(({ size, inStock }) => ({
      value: size,
      label: size.startsWith('0') ? size.replace('0', '') : size,
      inStock,
    }));

  // The list can span every finish (until one is selected) and the same size
  // may be out of stock in one finish but available in another — merge
  // duplicates with OR so a size stays selectable while any finish offers it.
  const uniqueOptions = allSizes.reduce((acc, option) => {
    const existing = acc.get(option.value);
    if (existing) {
      existing.inStock = existing.inStock || option.inStock;
    } else {
      acc.set(option.value, option);
    }
    return acc;
  }, new Map<string, SizeOption>());

  return sortBy(Array.from(uniqueOptions.values()), ({ value }) =>
    Number.parseInt(value),
  );
};

const useProductOptionsData = (
  products: Product[],
  { selectedSku, imageType }: UseProductOptionDataOptions = {},
): ProductOptionsData => {
  const finishOptions = useFinishOptions(products, { imageType });
  const sizeOptions = useSizeOptions(products, { selectedSku });

  return {
    finishOptions,
    sizeOptions,
  };
};

export default useProductOptionsData;
